import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '../components/ResponsiveDataTable';
import {
  deleteUser,
  getOmsUsers,
  getWarehouses,
  updateUserAccess,
  updateUserRole,
  updateUserStatus,
  updateUserWarehouseAccess,
} from '../config/api';
import { hasCrmAccess, hasCrmAdminAccess } from '../utils/crmAccess';
import './AccessManagement.css';

const roleDefinitions = [
  {
    key: 'superAdmin', label: 'Super Admin', icon: '🔧',
    points: ['Full system access and control', 'Manage all users, roles, and permissions', 'Access to all modules and features', 'View and manage all orders, products, and payments'],
  },
  {
    key: 'admin', label: 'Admin', icon: '🛡️',
    points: ['View and manage all orders', 'View protected order and customer details', 'Manage CRM settings, imports, and edit history', 'Access other modules through assigned permissions', 'Cannot manage users, roles, or permissions'],
  },
  {
    key: 'user', label: 'User', icon: '👤',
    points: ['Access only assigned modules', 'Work with owned CRM leads when CRM access is assigned', 'Use hub-based tools only for assigned warehouses', 'Cannot manage users or system settings'],
  },
];

const permissionDefinitions = [
  { key: 'marketing', label: 'Marketing Access', icon: '📈', tone: 'mint', points: ['View and manage products, kits, and listings', 'Manage coupons and deals banners', 'Manage audiences and WhatsApp campaigns'] },
  { key: 'finance', label: 'Finance Access', icon: '💰', tone: 'violet', points: ['View and manage payments and ledger entries', 'Approve wallet payments and add manual payments', 'Manage virtual accounts, GST, and invoice approvals'] },
  { key: 'pricing', label: 'Pricing Access', icon: '💲', tone: 'blue', points: ['View and edit product pricing', 'Manage warehouse pricing and price slabs', 'View pricing history'] },
  { key: 'crm', label: 'CRM Access', icon: '📇', tone: 'cyan', points: ['View CRM leads and lead details', 'Manage assigned contacts, follow-ups, calls, and WhatsApp', 'CRM Admin can manage leads across all hubs'] },
  { key: 'crmAdmin', label: 'CRM Admin Access', icon: '✦', tone: 'indigo', points: ['Includes CRM access automatically', 'Manage all CRM leads, settings, imports, and history', 'Access the CRM dashboard and elevated workflows'] },
  { key: 'hubManager', label: 'Hub Manager Access', icon: '🏢', tone: 'amber', points: ['Use hub-level order and invoice actions', 'Generate permitted invoices and e-way bills', 'Follow invoice ownership and daily-limit rules'] },
  { key: 'ops', label: 'Operations Access', icon: '⚙️', tone: 'rose', points: ['Access operational tools', 'Manage serial number and DCR workflows', 'Work within assigned warehouse access'] },
  { key: 'delivery', label: 'Delivery Access', icon: '🚚', tone: 'orange', points: ['Reserved for delivery-specific workflows', 'Currently does not unlock a separate module'] },
];

const accessOptions = [
  { value: 'marketing', label: 'Marketing', tone: 'mint' },
  { value: 'finance', label: 'Finance', tone: 'violet' },
  { value: 'ops', label: 'Operations', tone: 'rose' },
  { value: 'delivery', label: 'Delivery', tone: 'orange' },
  { value: 'pricing', label: 'Pricing', tone: 'blue' },
  { value: 'crm', label: 'CRM', tone: 'cyan' },
  { value: 'crmAdmin', label: 'CRM Admin', tone: 'indigo' },
  { value: 'hubManager', label: 'Hub Manager', tone: 'amber' },
];

const roleOptions = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
];

const normaliseLegacyManager = (user) => user.role === 'manager' ? {
  ...user,
  role: 'user',
  access: [...new Set([...(Array.isArray(user.access) ? user.access : []), 'crm', 'crmAdmin'])],
  legacyRole: 'manager',
} : user;

const isActiveUser = (user) => user.active === true || user.active === 'active' || user.active === 1;

const getInitials = (name = '') => name.trim().split(/\s+/).slice(0, 2)
  .map((part) => part[0]).join('').toUpperCase() || '?';

const CompactValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const requestedMax = props.selectProps.maxVisibleValues || 2;
  const maxVisible = props.selectProps.compactBelow
    && window.innerWidth < props.selectProps.compactBelow
    ? Math.max(1, requestedMax - 1)
    : requestedMax;
  if (!selected.length) return <components.ValueContainer {...props}>{children}</components.ValueContainer>;

  const childNodes = React.Children.toArray(children);
  const input = childNodes[childNodes.length - 1];
  const valueNodes = childNodes.slice(0, -1);
  const hiddenCount = Math.max(0, selected.length - maxVisible);
  return (
    <components.ValueContainer {...props}>
      {valueNodes.slice(0, maxVisible)}
      {hiddenCount > 0 && <span className="access-more-chip">+{hiddenCount}</span>}
      {input}
    </components.ValueContainer>
  );
};

const ChecklistOption = ({ children, isSelected, ...props }) => (
  <components.Option {...props} isSelected={isSelected}>
    <input type="checkbox" checked={isSelected} readOnly tabIndex={-1} aria-hidden="true" />
    <span>{children}</span>
  </components.Option>
);

const TitledMultiValueLabel = ({ children, ...props }) => (
  <components.MultiValueLabel {...props}>
    <span title={props.data.label}>{children}</span>
  </components.MultiValueLabel>
);

const AccessMenuList = ({ children, ...props }) => {
  const selectedCount = props.getValue().length;
  return (
    <components.MenuList {...props}>
      <div className="access-menu-actions">
        <button type="button" onMouseDown={(event) => {
          event.preventDefault(); event.stopPropagation(); props.selectProps.onSelectAll();
        }} disabled={selectedCount === accessOptions.length}>Select all</button>
        <span aria-hidden="true">/</span>
        <button type="button" onMouseDown={(event) => {
          event.preventDefault(); event.stopPropagation(); props.selectProps.onClearAll();
        }} disabled={selectedCount === 0}>Clear</button>
      </div>
      {children}
    </components.MenuList>
  );
};

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />
  </svg>
);

const RemoveAccessIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="8.5" /><path d="M8.5 12h7" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" />
  </svg>
);

const AccessManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'crm' ? 'crm' : 'oms';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [crmSearchInput, setCrmSearchInput] = useState('');
  const [crmFilterText, setCrmFilterText] = useState('');
  const [crmRoleFilter, setCrmRoleFilter] = useState('all');
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [saveFeedback, setSaveFeedback] = useState({});
  const feedbackTimers = useRef({});
  const loggedInUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');

  useEffect(() => {
    loadUsers();
    loadWarehouses();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilterText(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = window.setTimeout(() => setCrmFilterText(crmSearchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [crmSearchInput]);

  useEffect(() => () => {
    Object.values(feedbackTimers.current).forEach(window.clearTimeout);
  }, []);

  const showSaveState = (userId, state) => {
    if (feedbackTimers.current[userId]) window.clearTimeout(feedbackTimers.current[userId]);
    setSaveFeedback((current) => ({ ...current, [userId]: state }));
    if (state === 'saved') {
      feedbackTimers.current[userId] = window.setTimeout(() => {
        setSaveFeedback((current) => {
          const next = { ...current };
          delete next[userId];
          return next;
        });
      }, 2200);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        setWarehouseOptions(response.data.map((warehouse) => ({ value: warehouse.id, label: warehouse.name })));
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getOmsUsers() || {};
      setUsers((data.allUsers || []).map(normaliseLegacyManager));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessSelectionChange = async (currentUser, selectedOptions = []) => {
    const currentAccess = currentUser.access || [];
    let nextAccess = selectedOptions.map((option) => option.value);
    const removingCrmWithAdmin = currentAccess.includes('crmAdmin')
      && currentAccess.includes('crm')
      && !nextAccess.includes('crm');
    if (removingCrmWithAdmin) {
      const confirmed = window.confirm('Removing CRM also removes CRM Admin. Continue?');
      if (!confirmed) return;
      nextAccess = nextAccess.filter((permission) => permission !== 'crmAdmin');
    }
    if (nextAccess.includes('crmAdmin') && !nextAccess.includes('crm')) {
      nextAccess = [...nextAccess, 'crm'];
    }
    const added = nextAccess.filter((accessType) => !currentAccess.includes(accessType));
    const removed = currentAccess.filter((accessType) => !nextAccess.includes(accessType));
    if (!added.length && !removed.length) return;

    try {
      setUpdating(true);
      showSaveState(currentUser.id, 'saving');
      for (const accessType of added) await updateUserAccess(currentUser.id, accessType, true);
      for (const accessType of removed) await updateUserAccess(currentUser.id, accessType, false);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id ? {
        ...user,
        access: nextAccess,
        access_changed_at: new Date().toISOString(),
        access_changed_by: loggedInUser.id,
      } : user));
      showSaveState(currentUser.id, 'saved');
      toast.success(`Access updated for ${currentUser.name}`);
    } catch (error) {
      console.error('Error updating user access:', error);
      showSaveState(currentUser.id, 'error');
      toast.error('Failed to update user access. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleRoleChange = async (userId, newRole, currentUser) => {
    const roleLabel = roleOptions.find((option) => option.value === newRole)?.label || newRole;
    if (!window.confirm(`Are you sure you want to change ${currentUser.name}'s role to ${roleLabel}?`)) return;
    try {
      setUpdating(true);
      showSaveState(userId, 'saving');
      await updateUserRole(userId, newRole);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === userId ? {
        ...user,
        role: newRole,
        role_changed_at: new Date().toISOString(),
        role_changed_by: loggedInUser.id,
      } : user));
      showSaveState(userId, 'saved');
      window.alert(`Successfully updated ${currentUser.name}'s role to ${roleLabel}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      showSaveState(userId, 'error');
      window.alert('Failed to update user role. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (userId, newStatus, currentUser) => {
    const statusText = newStatus ? 'active' : 'inactive';
    try {
      setUpdating(true);
      showSaveState(userId, 'saving');
      await updateUserStatus(userId, newStatus);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === userId ? {
        ...user,
        active: newStatus,
        status_changed_at: new Date().toISOString(),
        status_changed_by: loggedInUser.id,
      } : user));
      showSaveState(userId, 'saved');
      window.alert(`Successfully updated ${currentUser.name}'s status to ${statusText}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      showSaveState(userId, 'error');
      window.alert('Failed to update user status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleWarehouseAccessChange = async (currentUser, selectedOptions = []) => {
    const currentWarehouseAccess = currentUser.warehouse_access || [];
    const nextWarehouseAccess = selectedOptions.map((option) => option.value);
    const added = nextWarehouseAccess.filter((id) => !currentWarehouseAccess.includes(id));
    const removed = currentWarehouseAccess.filter((id) => !nextWarehouseAccess.includes(id));
    if (!added.length && !removed.length) return;

    try {
      setUpdating(true);
      showSaveState(currentUser.id, 'saving');
      for (const warehouseId of added) await updateUserWarehouseAccess(currentUser.id, warehouseId, true);
      for (const warehouseId of removed) await updateUserWarehouseAccess(currentUser.id, warehouseId, false);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id ? {
        ...user,
        warehouse_access: nextWarehouseAccess,
        access_changed_at: new Date().toISOString(),
        access_changed_by: loggedInUser.id,
      } : user));
      showSaveState(currentUser.id, 'saved');
    } catch (error) {
      console.error('Error updating warehouse access:', error);
      showSaveState(currentUser.id, 'error');
      window.alert('Failed to update warehouse access. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCrmAdminChange = async (currentUser, isGranted) => {
    const previousAccess = Array.isArray(currentUser.access) ? currentUser.access : [];
    const nextAccess = isGranted
      ? [...new Set([...previousAccess, 'crm', 'crmAdmin'])]
      : previousAccess.filter((permission) => permission !== 'crmAdmin');

    setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id
      ? { ...user, access: nextAccess }
      : user));
    setUpdating(true);
    showSaveState(currentUser.id, 'saving');
    try {
      await updateUserAccess(currentUser.id, 'crmAdmin', isGranted);
      const changedAt = new Date().toISOString();
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id ? {
        ...user,
        access: nextAccess,
        updatedAt: changedAt,
        changed_by: loggedInUser.id,
      } : user));
      showSaveState(currentUser.id, 'saved');
      toast.success(`${currentUser.name} is now ${isGranted ? 'a CRM Admin' : 'a CRM Agent'}`);
    } catch (error) {
      console.error('Error updating CRM Admin access:', error);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id
        ? { ...user, access: previousAccess }
        : user));
      showSaveState(currentUser.id, 'error');
      toast.error('Failed to update CRM Admin access. The change was rolled back.');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveCrmAccess = async (currentUser) => {
    if (!window.confirm(`Remove ${currentUser.name} from CRM access? They will lose CRM and CRM Admin access.`)) return;
    const previousAccess = Array.isArray(currentUser.access) ? currentUser.access : [];
    const nextAccess = previousAccess.filter((permission) => !['crm', 'crmAdmin'].includes(permission));

    setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id
      ? { ...user, access: nextAccess }
      : user));
    setUpdating(true);
    showSaveState(currentUser.id, 'saving');
    try {
      await updateUserAccess(currentUser.id, 'crm', false);
      const changedAt = new Date().toISOString();
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id ? {
        ...user,
        access: nextAccess,
        updatedAt: changedAt,
        changed_by: loggedInUser.id,
      } : user));
      showSaveState(currentUser.id, 'saved');
      toast.success(`${currentUser.name} was removed from CRM access`);
    } catch (error) {
      console.error('Error removing CRM access:', error);
      setUsers((currentUsers) => currentUsers.map((user) => user.id === currentUser.id
        ? { ...user, access: previousAccess }
        : user));
      showSaveState(currentUser.id, 'error');
      toast.error('Failed to remove CRM access. The change was rolled back.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId, currentUser) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete user "${currentUser.name}"?`)) return;
    try {
      setUpdating(true);
      const response = await deleteUser(userId);
      if (response.success) {
        setUsers((currentUsers) => currentUsers.filter((user) => user.id !== userId));
        window.alert(`Successfully deleted user: ${currentUser.name}`);
      } else window.alert(response.message || 'Failed to delete user');
    } catch (error) {
      console.error('Error deleting user:', error);
      window.alert('Failed to delete user. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { short: 'Never', full: 'Never' };
    const date = new Date(dateString);
    return {
      short: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      full: date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
  };

  const getChangedByName = (userId) => {
    if (!userId) return 'System';
    return users.find((user) => user.id === userId)?.name || 'Unknown';
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base, minHeight: 36, height: 36, borderColor: state.isFocused ? '#6385f5' : '#d9dee7', borderRadius: 8,
      boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 111, 232, .15)' : 'none',
      backgroundColor: state.isDisabled ? '#f4f6f8' : '#fff', cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': { borderColor: state.isFocused ? '#6385f5' : '#b8c0cc' },
    }),
    valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', gap: 3, padding: '2px 7px', overflow: 'hidden' }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    dropdownIndicator: (base) => ({ ...base, padding: 5, color: '#7b8493' }),
    clearIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (base) => ({ ...base, color: '#667085', fontSize: 12, whiteSpace: 'nowrap' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    multiValue: (base, state) => ({ ...base, flex: '0 0 auto', maxWidth: 86, margin: 0, borderRadius: 999, backgroundColor: state.data.tone ? `var(--access-${state.data.tone}-bg)` : '#eef1f5' }),
    multiValueLabel: (base, state) => ({ ...base, overflow: 'hidden', padding: '3px 6px', color: state.data.tone ? `var(--access-${state.data.tone}-text)` : '#455064', fontSize: 10, fontWeight: 650, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
    multiValueRemove: (base, state) => ({ ...base, padding: '0 3px', borderRadius: '0 999px 999px 0', color: state.data.tone ? `var(--access-${state.data.tone}-text)` : '#667085', ':hover': { backgroundColor: 'rgba(15, 23, 42, .08)', color: '#111827' } }),
    menu: (base) => ({ ...base, zIndex: 9999, minWidth: 210, borderRadius: 9, overflow: 'hidden', fontSize: 12 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({ ...base, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', color: '#344054', backgroundColor: state.isFocused ? '#f2f5fa' : '#fff', cursor: 'pointer', ':active': { backgroundColor: '#e8edf6' } }),
  };

  const warehouseSelectStyles = {
    ...selectStyles,
    multiValue: (base, state) => ({
      ...selectStyles.multiValue(base, state),
      maxWidth: 'clamp(90px, 7vw, 160px)',
    }),
  };

  const renderReadOnlyWarehouses = (row) => {
    const selected = warehouseOptions.filter((warehouse) => (row.warehouse_access || []).includes(warehouse.value));
    const fullList = selected.map((warehouse) => warehouse.label).join(', ') || 'All warehouses';
    if (!selected.length) return <span className="crm-all-warehouses">All warehouses</span>;
    return (
      <div className="crm-warehouse-chips" title={fullList} aria-label={`Warehouses: ${fullList}`}>
        {selected.slice(0, 2).map((warehouse) => (
          <span className="crm-warehouse-chip" key={warehouse.value} title={warehouse.label}>{warehouse.label}</span>
        ))}
        {selected.length > 2 && <span className="access-more-chip">+{selected.length - 2}</span>}
      </div>
    );
  };

  const columns = [
    {
      id: 'name', name: 'User', selector: (row) => row.name, sortable: true, width: '22%', minWidth: '240px',
      cell: (row) => (
        <div className="access-user-cell">
          <span className="access-avatar" aria-hidden="true">{getInitials(row.name)}</span>
          <span className="access-user-copy">
            <span className="access-user-name" title={row.name}>{row.name}</span>
            <span className="access-user-email" title={row.email}>{row.email}</span>
          </span>
        </div>
      ),
    },
    {
      id: 'role', name: 'Role', selector: (row) => row.role, sortable: true, width: '10%', minWidth: '130px', ignoreRowClick: true,
      cell: (row) => {
        const isLocked = updating || row.role === 'superAdmin' || String(row.id) === String(loggedInUser.id);
        return (
          <select value={row.role} onChange={(event) => handleRoleChange(row.id, event.target.value, row)}
            disabled={isLocked} className={`access-role-select role-${row.role}`} aria-label={`Role for ${row.name}`}
            title={row.role === 'superAdmin' ? 'Super Admin role is locked' : isLocked ? 'You cannot change your own role' : `Change ${row.name}'s role`}>
            {row.role === 'superAdmin' && <option value="superAdmin">Super Admin</option>}
            {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        );
      },
    },
    {
      id: 'status', name: 'Status', selector: (row) => isActiveUser(row), sortable: true, width: '8%', minWidth: '110px', ignoreRowClick: true,
      cell: (row) => {
        const active = isActiveUser(row);
        const isLocked = updating || row.role === 'superAdmin' || String(row.id) === String(loggedInUser.id);
        return (
          <label className={`access-status-control${isLocked ? ' is-disabled' : ''}`}>
            <input type="checkbox" checked={active} onChange={(event) => handleStatusChange(row.id, event.target.checked, row)}
              disabled={isLocked} aria-label={`${row.name} is ${active ? 'active' : 'inactive'}`} />
            <span className="access-toggle" aria-hidden="true"><span /></span>
            <span className={active ? 'is-active' : ''}>{active ? 'Active' : 'Inactive'}</span>
          </label>
        );
      },
    },
    {
      id: 'access', name: 'Access', selector: (row) => row.access, width: '24%', minWidth: '260px', ignoreRowClick: true, allowOverflow: true,
      cell: (row) => {
        const selectedAccess = row.role === 'superAdmin'
          ? accessOptions
          : accessOptions.filter((option) => (row.access || []).includes(option.value));
        const fullList = selectedAccess.map((option) => option.label).join(', ') || 'No access assigned';
        return (
          <div className="access-compact-select" title={fullList}>
            <Select inputId={`access-select-${row.id}`} aria-label={`Access permissions for ${row.name}`}
              value={selectedAccess} onChange={(selected) => handleAccessSelectionChange(row, selected || [])}
              options={accessOptions} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} isClearable={false}
              isDisabled={updating || row.role === 'superAdmin'} placeholder="No access" maxVisibleValues={3} compactBelow={1440}
              onSelectAll={() => handleAccessSelectionChange(row, accessOptions)} onClearAll={() => handleAccessSelectionChange(row, [])}
              components={{ ValueContainer: CompactValueContainer, MultiValueLabel: TitledMultiValueLabel, Option: ChecklistOption, MenuList: AccessMenuList }}
              styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
        );
      },
    },
    {
      id: 'warehouses', name: 'Warehouses', width: '22%', minWidth: '240px', ignoreRowClick: true, allowOverflow: true,
      cell: (row) => {
        const selectedWarehouses = warehouseOptions.filter((warehouse) => (row.warehouse_access || []).includes(warehouse.value));
        const fullList = selectedWarehouses.map((warehouse) => warehouse.label).join(', ') || 'All warehouses';
        return (
          <div className="access-compact-select" title={fullList}>
            <Select inputId={`warehouse-select-${row.id}`} aria-label={`Warehouse access for ${row.name}`}
              value={selectedWarehouses} onChange={(selected) => handleWarehouseAccessChange(row, selected || [])}
              options={warehouseOptions} isMulti isClearable={false} isDisabled={updating} placeholder="All warehouses" maxVisibleValues={2}
              components={{ ValueContainer: CompactValueContainer, MultiValueLabel: TitledMultiValueLabel }} styles={warehouseSelectStyles}
              menuPortalTarget={document.body} menuPosition="fixed" />
          </div>
        );
      },
    },
    {
      id: 'updated', name: 'Updated', selector: (row) => row.updatedAt, sortable: true, width: '10%', minWidth: '120px',
      cell: (row) => {
        const changed = formatDateTime(row.updatedAt);
        const feedback = saveFeedback[row.id];
        return (
          <div className="access-updated-cell">
            <span title={changed.full}>{changed.short}</span>
            {row.updatedAt && <small title={`Updated by ${getChangedByName(row.changed_by)}`}>by {getChangedByName(row.changed_by)}</small>}
            {feedback && <span className={`access-save-feedback is-${feedback}`} aria-live="polite">
              {feedback === 'saving' && <span className="access-inline-spinner" aria-hidden="true" />}
              {feedback === 'saving' ? 'Saving' : feedback === 'saved' ? '✓ Saved' : 'Save failed'}
            </span>}
          </div>
        );
      },
    },
    {
      id: 'actions', name: '', width: '4%', minWidth: '56px', ignoreRowClick: true, allowOverflow: true,
      cell: (row) => {
        const isSelf = String(row.id) === String(loggedInUser.id);
        return (
          <button type="button" onClick={() => handleDeleteUser(row.id, row)} disabled={updating || isSelf}
            className="access-delete-button" title={isSelf ? 'Cannot delete your own account' : `Delete ${row.name}`}
            aria-label={isSelf ? 'Cannot delete your own account' : `Delete ${row.name}`}><TrashIcon /></button>
        );
      },
    },
  ];

  const crmColumns = [
    {
      id: 'crm-name', name: 'User', selector: (row) => row.name, sortable: true, width: '31%', minWidth: '240px',
      cell: (row) => (
        <div className="access-user-cell">
          <span className="access-avatar" aria-hidden="true">{getInitials(row.name)}</span>
          <span className="access-user-copy">
            <span className="access-user-name" title={row.name}>{row.name}</span>
            <span className="access-user-email" title={row.email}>{row.email}</span>
          </span>
        </div>
      ),
    },
    {
      id: 'crm-role', name: 'CRM Role', selector: (row) => hasCrmAdminAccess(row) ? 'admin' : 'agent', sortable: true,
      width: '16%', minWidth: '140px',
      cell: (row) => <span className={`crm-role-badge ${hasCrmAdminAccess(row) ? 'is-admin' : 'is-agent'}`}>
        {hasCrmAdminAccess(row) ? 'CRM Admin' : 'CRM Agent'}
      </span>,
    },
    {
      id: 'crm-warehouses', name: 'Warehouses', width: '25%', minWidth: '240px',
      cell: (row) => renderReadOnlyWarehouses(row),
    },
    {
      id: 'crm-updated', name: 'Updated', selector: (row) => row.updatedAt, sortable: true, width: '14%', minWidth: '130px',
      cell: (row) => {
        const changed = formatDateTime(row.updatedAt);
        const feedback = saveFeedback[row.id];
        return (
          <div className="access-updated-cell">
            <span title={changed.full}>{changed.short}</span>
            {row.updatedAt && <small title={`Updated by ${getChangedByName(row.changed_by)}`}>by {getChangedByName(row.changed_by)}</small>}
            {feedback && <span className={`access-save-feedback is-${feedback}`} aria-live="polite">
              {feedback === 'saving' && <span className="access-inline-spinner" aria-hidden="true" />}
              {feedback === 'saving' ? 'Saving' : feedback === 'saved' ? '✓ Saved' : 'Save failed'}
            </span>}
          </div>
        );
      },
    },
    {
      id: 'crm-actions', name: 'Actions', width: '14%', minWidth: '180px', ignoreRowClick: true, allowOverflow: true,
      cell: (row) => {
        const isRoleLocked = ['admin', 'superAdmin'].includes(row.role);
        const isAdmin = hasCrmAdminAccess(row);
        return (
          <div className="crm-actions">
            <label className={`crm-admin-control${isRoleLocked ? ' is-disabled' : ''}`}
              title={isRoleLocked ? `${row.role === 'superAdmin' ? 'Super Admin' : 'Admin'} has CRM Admin access through their role` : 'Toggle CRM Admin access'}>
              <input type="checkbox" checked={isAdmin} disabled={updating || isRoleLocked}
                onChange={(event) => handleCrmAdminChange(row, event.target.checked)}
                aria-label={`CRM Admin access for ${row.name}`} />
              <span className="access-toggle" aria-hidden="true"><span /></span>
              <span>Admin</span>
            </label>
            <button type="button" className="crm-remove-button" disabled={updating || isRoleLocked}
              onClick={() => handleRemoveCrmAccess(row)}
              title={isRoleLocked ? `${row.role === 'superAdmin' ? 'Super Admin' : 'Admin'} CRM access is role-based` : `Remove ${row.name} from CRM access`}
              aria-label={`Remove ${row.name} from CRM access`}><RemoveAccessIcon /></button>
          </div>
        );
      },
    },
  ];

  const filteredItems = useMemo(() => {
    const query = filterText.toLowerCase();
    return users.filter((user) => {
      const matchesSearch = !query || [user.name, user.email, user.role, isActiveUser(user) ? 'active' : 'inactive', ...(user.access || [])]
        .some((value) => String(value || '').toLowerCase().includes(query));
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? isActiveUser(user) : !isActiveUser(user));
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, filterText, roleFilter, statusFilter]);

  const crmUsers = useMemo(() => users.filter((user) => hasCrmAccess(user)), [users]);

  const crmFilteredItems = useMemo(() => {
    const query = crmFilterText.toLowerCase();
    return crmUsers.filter((user) => {
      const isAdmin = hasCrmAdminAccess(user);
      const matchesSearch = !query || [user.name, user.email]
        .some((value) => String(value || '').toLowerCase().includes(query));
      const matchesRole = crmRoleFilter === 'all' || (crmRoleFilter === 'admin' ? isAdmin : !isAdmin);
      return matchesSearch && matchesRole;
    });
  }, [crmUsers, crmFilterText, crmRoleFilter]);

  const customStyles = {
    table: { style: { tableLayout: 'fixed' } },
    subHeader: { style: { minHeight: '64px', padding: 0, backgroundColor: '#fff' } },
    headRow: { style: { minHeight: '42px', position: 'sticky', top: 0, zIndex: 4, borderBottom: '1px solid #dfe4eb', backgroundColor: '#f8f9fb' } },
    headCells: { style: { padding: '0 10px', color: '#697386', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' } },
    cells: { style: { minWidth: 0, padding: '8px 10px', color: '#344054', fontSize: 12, overflow: 'visible' } },
    rows: { style: { minHeight: '70px', maxHeight: '70px', borderBottom: '1px solid #edf0f4', '&:nth-of-type(even)': { backgroundColor: '#fbfcfd' }, '&:hover': { backgroundColor: '#f4f7fb' } } },
    pagination: { style: { minHeight: '54px', padding: '0 12px', borderTop: '1px solid #e7eaf0', color: '#596579', fontSize: 12 } },
  };

  const omsToolbar = (
    <div className="access-toolbar">
      <div className="access-search-wrap">
        <SearchIcon />
        <label className="sr-only" htmlFor="access-user-search">Search users</label>
        <input id="access-user-search" type="search" placeholder="Search name, email, role or access…"
          value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
      </div>
      <div className="access-toolbar-right">
        <span className="access-result-count" aria-live="polite">Showing {filteredItems.length} of {users.length} users</span>
        <label><span className="sr-only">Filter by role</span>
          <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="all">All roles</option><option value="superAdmin">Super Admin</option>
            {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label><span className="sr-only">Filter by status</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </label>
      </div>
    </div>
  );

  const crmToolbar = (
    <div className="access-toolbar">
      <div className="access-search-wrap">
        <SearchIcon />
        <label className="sr-only" htmlFor="crm-access-user-search">Search CRM users</label>
        <input id="crm-access-user-search" type="search" placeholder="Search CRM users by name or email…"
          value={crmSearchInput} onChange={(event) => setCrmSearchInput(event.target.value)} />
      </div>
      <div className="access-toolbar-right">
        <span className="access-result-count" aria-live="polite">Showing {crmFilteredItems.length} of {crmUsers.length} users</span>
        <label><span className="sr-only">Filter by CRM role</span>
          <select value={crmRoleFilter} onChange={(event) => setCrmRoleFilter(event.target.value)}>
            <option value="all">All CRM roles</option>
            <option value="admin">CRM Admin</option>
            <option value="agent">CRM Agent</option>
          </select>
        </label>
      </div>
    </div>
  );

  const changeTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const handleTabKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextTab = ['ArrowRight', 'End'].includes(event.key) ? 'crm' : 'oms';
    changeTab(nextTab);
    window.requestAnimationFrame(() => document.getElementById(`access-tab-${nextTab}`)?.focus());
  };

  return (
    <main className="access-page">
      <header className="access-page-header">
        <div><h1>Access Management</h1><p>Manage user roles, status, permissions and warehouse access.</p></div>
        <span className="access-admin-notice"><span aria-hidden="true">◈</span> Super Admin Only</span>
      </header>

      <div className="access-tabs" role="tablist" aria-label="Access management sections">
        <button type="button" role="tab" id="access-tab-oms" aria-selected={activeTab === 'oms'}
          aria-controls="access-panel-oms" tabIndex={activeTab === 'oms' ? 0 : -1}
          className={activeTab === 'oms' ? 'is-active' : ''} onClick={() => changeTab('oms')} onKeyDown={handleTabKeyDown}>
          OMS Access <span>{users.length}</span>
        </button>
        <button type="button" role="tab" id="access-tab-crm" aria-selected={activeTab === 'crm'}
          aria-controls="access-panel-crm" tabIndex={activeTab === 'crm' ? 0 : -1}
          className={activeTab === 'crm' ? 'is-active' : ''} onClick={() => changeTab('crm')} onKeyDown={handleTabKeyDown}>
          CRM Access <span>{crmUsers.length}</span>
        </button>
      </div>

      {activeTab === 'oms' && <div role="tabpanel" id="access-panel-oms" aria-labelledby="access-tab-oms">
        <section className="access-table-card" aria-label="OMS user access management">
          <div className="access-table-scroll"><div className="access-table-inner">
            <DataTable className="access-data-table" columns={columns} data={filteredItems} progressPending={loading}
              progressComponent={<div className="access-loading"><span className="access-inline-spinner" /> Loading users…</div>}
              pagination paginationResetDefaultPage={false} subHeader subHeaderComponent={omsToolbar} selectableRows={false}
              customStyles={customStyles} responsive paginationPerPage={10} paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
              noDataComponent={<div className="access-empty-state"><strong>No users found</strong><span>Try changing your search or filters.</span></div>}
              defaultSortFieldId="name" defaultSortAsc mobileSummaryFields={['User', 'Role', 'Status', 'Access', 'Warehouses', 'Updated']} />
          </div></div>
        </section>

        <section className="card access-guide">
        <div className="access-guide-header">
          <div><h3 className="section-title">Role &amp; Access Definitions</h3><p>Roles define authority. Permissions unlock modules. Warehouse access applies to hub-based operations.</p></div>
          <span className="access-guide-badge">Reference guide</span>
        </div>
        <div className="role-definition-grid">
          {roleDefinitions.map((role) => <article className={`role-definition-card role-${role.key}`} key={role.key}>
            <h4>{role.icon} {role.label}</h4><ul>{role.points.map((point) => <li key={point}>{point}</li>)}</ul>
          </article>)}
        </div>
        <div className="permission-guide">
          <div className="permission-guide-title"><div><h4>Access permissions</h4><p>These permissions are assigned separately from the user’s role.</p></div><span>{permissionDefinitions.length} permissions</span></div>
          <div className="permission-definition-grid">
            {permissionDefinitions.map((permission) => <article className={`permission-definition-card tone-${permission.tone}`} key={permission.key}>
              <div className="permission-definition-heading"><h5>{permission.icon} {permission.label}</h5></div>
              <ul>{permission.points.map((point) => <li key={point}>{point}</li>)}</ul>
            </article>)}
          </div>
        </div>
          <div className="access-guide-note"><strong>Warehouse access:</strong> selected warehouses limit hub-based operations. No selection means all warehouses. CRM Admin access provides elevated CRM workflows.</div>
        </section>
      </div>}

      {activeTab === 'crm' && <div role="tabpanel" id="access-panel-crm" aria-labelledby="access-tab-crm">
        <div className="crm-access-note"><strong>CRM access:</strong> CRM Admin includes CRM automatically. Removing CRM also removes CRM Admin without changing any other OMS permissions.</div>
        <section className="access-table-card crm-access-card" aria-label="CRM access management">
          <div className="access-table-scroll"><div className="access-table-inner crm-table-inner">
            <DataTable className="access-data-table" columns={crmColumns} data={crmFilteredItems} progressPending={loading}
              progressComponent={<div className="access-loading"><span className="access-inline-spinner" /> Loading CRM users…</div>}
              pagination paginationResetDefaultPage={false} subHeader subHeaderComponent={crmToolbar} selectableRows={false}
              customStyles={customStyles} responsive paginationPerPage={10} paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
              noDataComponent={<div className="access-empty-state"><strong>No users have CRM access yet</strong><span>Assign CRM or CRM Admin from the OMS Access tab.</span></div>}
              defaultSortFieldId="crm-name" defaultSortAsc mobileSummaryFields={['User', 'CRM Role', 'Warehouses', 'Updated', 'Actions']} />
          </div></div>
        </section>
      </div>}
    </main>
  );
};

export default AccessManagement;
